import { morph } from '@theme/morph';

class ContentSearchResults extends HTMLElement {
  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #loadingController = null;

  #page = 1;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#loadingController?.abort();

    this.#page = Number(new URLSearchParams(window.location.search).get('page')) || 1;

    this.querySelector('pagination-custom')?.addEventListener(
      'change',
      this.onPageChange.bind(this),
      { signal: this.#controller.signal }
    );

    const { signal } = this.#controller;

    window.addEventListener('locationchange', this.onLocationChange.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#loadingController?.abort();
  }

  /**
   * Updates the search parameters in the URL.
   * @param {Object} params - The search parameters to update.
   * @param {number} params.page - The current page number.
   */
  _updateSearchParams({ page }) {
    const params = new URLSearchParams(window.location.search);

    if (String(page) === params.get('page')) return;

    params.set('page', String(page));
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params}${window.location.hash}`
    );
  }

  /**
   * Handle page change events.
   * @param {CustomEvent} e - The page change event.
   */
  onPageChange(e) {
    if (!(e instanceof CustomEvent)) return;
    const { page } = e.detail;
    if (typeof page !== 'number' || page === this.#page) return;
    this._updateSearchParams({ page });
  }

  onLocationChange() {
    const newPage = Number(new URLSearchParams(window.location.search).get('page')) || 1;
    if (newPage === this.#page) return;
    this.#page = newPage;
    this._loadSection();
  }

  async _loadSection() {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) {
      console.warn('No section ID provided for content search results.');
      return;
    }

    const spinner = document.getElementById('search-results-posts-panel-spinner');

    if (spinner) spinner.style.display = 'block';

    window.scroll({
      top: 0,
      behavior: 'smooth',
    });

    this.#loadingController?.abort('cancelled');

    const controller = new AbortController();
    this.#loadingController = controller;

    try {
      const path = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sections', sectionId);
      const response = await fetch(`${path}?${searchParams.toString()}`, {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const html = data?.[sectionId];

      if (!html) {
        console.error(`No data found for section ID: ${sectionId}`);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(html, 'text/html');

      const fresh = newSectionHTML.querySelector('content-search-results section');
      const current = this.querySelector('section');

      if (current && fresh) {
        morph(current, fresh);
      }
    } catch (error) {
      if (error?.name === 'AbortError' || String(error?.message).includes('aborted')) return;
      console.error('Error loading section:', error);
    } finally {
      if (this.#loadingController === controller) {
        this.#loadingController = null;
      }
      if (spinner) spinner.style.display = 'none';
    }
  }
}

customElements.define('content-search-results', ContentSearchResults);
